// src/pages/admin/AdminCapelas.js

import React, { useEffect, useState } from 'react';
import { getCapelas, createCapela, updateCapela, deleteCapela } from '../../services/api';
import Toast from '../../components/common/Toast';

const UPLOAD_CHAVE = 'paroquia2024';
const UPLOAD_URL = 'https://www.paroquiademessejana.com.br/upload.php';

const initialForm = { nome: '', endereco: '', horarios: '', descricao: '' };

const FotoUpload = ({ label, preview, onFile, onRemove }) => (
  <div>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.4rem' }}>{label}</label>
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
      {preview && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <img src={preview} alt={label}
            style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '2px solid var(--gold)' }} />
          <button type="button" onClick={onRemove}
            style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', border: 'none', color: 'white', borderRadius: '50%', width: 20, height: 20, fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </div>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 90, height: 68, border: '2px dashed var(--gray-200)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--gray-400)', gap: '0.2rem', background: 'var(--off-white)' }}>
        <span style={{ fontSize: '1.25rem' }}>📷</span>
        <span>{preview ? 'Trocar' : 'Escolher'}</span>
        <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onFile} style={{ display: 'none' }} />
      </label>
    </div>
  </div>
);

const AdminCapelas = () => {
  const [capelas, setCapelas] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [fotos, setFotos] = useState({ foto: null, foto2: null, foto3: null });
  const [previews, setPreviews] = useState({ foto: null, foto2: null, foto3: null });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    getCapelas().then(r => setCapelas(r?.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const handleFoto = (campo) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFotos(f => ({ ...f, [campo]: file }));
    setPreviews(p => ({ ...p, [campo]: URL.createObjectURL(file) }));
  };

  const removeFoto = (campo) => {
    setFotos(f => ({ ...f, [campo]: null }));
    setPreviews(p => ({ ...p, [campo]: null }));
  };

  const uploadFoto = async (file) => {
    const fd = new FormData();
    fd.append('foto', file);
    fd.append('chave', UPLOAD_CHAVE);
    fd.append('pasta', 'comunidades');
    const res = await fetch(UPLOAD_URL, { method: 'POST', body: fd });
    const json = await res.json();
    if (json.success) return json.url;
    throw new Error(json.message || 'Erro no upload.');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    try {
      // Upload das fotos que foram alteradas
      const urls = {};
      for (const campo of ['foto', 'foto2', 'foto3']) {
        if (fotos[campo]) {
          urls[campo] = await uploadFoto(fotos[campo]);
        } else {
          // Mantém a URL existente se não trocou
          urls[campo] = previews[campo] && !fotos[campo] ? previews[campo] : '';
        }
      }

      const dados = {
        nome:      form.nome,
        endereco:  form.endereco || '',
        horarios:  form.horarios || '',
        descricao: form.descricao || '',
        foto:      urls.foto,
        foto2:     urls.foto2,
        foto3:     urls.foto3,
      };

      if (editId) {
        await updateCapela({ ...dados, id: editId });
        setToast({ msg: 'Comunidade atualizada!', type: 'success' });
      } else {
        await createCapela(dados);
        setToast({ msg: 'Comunidade cadastrada!', type: 'success' });
      }

      cancelar();
      load();
    } catch (err) {
      setToast({ msg: err.message || 'Erro ao salvar.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta comunidade?')) return;
    try {
      await deleteCapela(id);
      setToast({ msg: 'Comunidade excluída.', type: 'success' });
      load();
    } catch {
      setToast({ msg: 'Erro ao excluir.', type: 'error' });
    }
  };

  const handleEdit = (c) => {
    setForm({ nome: c.nome || '', endereco: c.endereco || '', horarios: c.horarios || '', descricao: c.descricao || '' });
    setEditId(c.id);
    setFotos({ foto: null, foto2: null, foto3: null });
    setPreviews({ foto: c.foto || null, foto2: c.foto2 || null, foto3: c.foto3 || null });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelar = () => {
    setShowForm(false);
    setEditId(null);
    setForm(initialForm);
    setFotos({ foto: null, foto2: null, foto3: null });
    setPreviews({ foto: null, foto2: null, foto3: null });
  };

  return (
    <div style={{ padding: 'var(--space-2xl)' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>Comunidades</h2>
          <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>{capelas.length} comunidade{capelas.length !== 1 ? 's' : ''} cadastrada{capelas.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { cancelar(); setShowForm(true); }}>
          + Nova Comunidade
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-md)', marginBottom: 'var(--space-xl)', border: '2px solid var(--gold-pale)' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)', fontSize: '1.1rem' }}>
            {editId ? '✏️ Editar Comunidade' : '+ Nova Comunidade'}
          </h3>
          <form onSubmit={handleSave} noValidate>

            {/* Dados */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nome <span className="required">*</span></label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} className="form-input" required />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Endereço</label>
                <input value={form.endereco} onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))} className="form-input" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Horários de Celebração</label>
              <textarea value={form.horarios} onChange={e => setForm(f => ({ ...f, horarios: e.target.value }))}
                className="form-textarea" placeholder="Ex: Domingo 8h&#10;Sábado 18h" rows={2} />
            </div>

            <div className="form-group">
              <label className="form-label">Descrição</label>
              <textarea value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                className="form-textarea" rows={3} />
            </div>

            {/* Fotos */}
            <div className="form-group">
              <label className="form-label">Fotos da Comunidade</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-lg)' }}>
                <FotoUpload label="Foto principal"
                  preview={previews.foto}
                  onFile={handleFoto('foto')}
                  onRemove={() => removeFoto('foto')} />
                <FotoUpload label="Foto 2 (opcional)"
                  preview={previews.foto2}
                  onFile={handleFoto('foto2')}
                  onRemove={() => removeFoto('foto2')} />
                <FotoUpload label="Foto 3 (opcional)"
                  preview={previews.foto3}
                  onFile={handleFoto('foto3')}
                  onRemove={() => removeFoto('foto3')} />
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>
                JPG, PNG ou WEBP · Máximo 10MB cada · As fotos aparecem no popup ao clicar na comunidade
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-sm"
                style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}
                onClick={cancelar}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Salvando...' : editId ? 'Salvar alterações' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Grid */}
      {capelas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--gray-400)' }}>Nenhuma comunidade cadastrada ainda.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--space-lg)' }}>
          {capelas.map((c, i) => (
            <div key={i} style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden', borderTop: '3px solid var(--gold)' }}>
              <div style={{ height: '130px', background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                {c.foto
                  ? <img src={c.foto} alt={c.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                  : <span style={{ fontSize: '2.5rem', opacity: 0.25 }}>⛪</span>}
                {/* Indicador de fotos extras */}
                {(c.foto2 || c.foto3) && (
                  <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', borderRadius: '99px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', color: 'white' }}>
                    +{[c.foto2, c.foto3].filter(Boolean).length} foto{[c.foto2, c.foto3].filter(Boolean).length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div style={{ padding: 'var(--space-md)' }}>
                <h4 style={{ marginBottom: '0.25rem', fontSize: '0.95rem', color: 'var(--navy)' }}>{c.nome}</h4>
                {c.endereco && <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginBottom: '0.75rem' }}>📍 {c.endereco}</p>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-sm btn-navy" style={{ flex: 1 }} onClick={() => handleEdit(c)}>Editar</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCapelas;